"use client";

import React, { useEffect, useState } from "react";
import {
  EcommerceProductsProvider,
  useEcommerceProducts,
  AddToCartButton,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { Loader, Tag, Folder, CircleAlert } from "lucide-react";


declare global {
  interface Window {
    wvcClient: any;
  }
}

function SingleProductContent() {
  const { products, loading, error, refetch } = useEcommerceProducts();

  if (loading) {
    return (
      <div data-section-id="863" className="flex min-h-[60vh] w-full items-center justify-center">
        <Loader className="text-primary h-10 w-10 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[40vh] w-full flex-col items-center justify-center gap-4 text-center">
        <CircleAlert className="text-destructive h-10 w-10" />
        <p className="text-muted-foreground">
          We couldn't hook the product details. Please try again.
        </p>
        <Button onClick={() => refetch()} variant="outline" className="border-primary text-primary hover:bg-primary/5">
          Try Again
        </Button>
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <div className="flex min-h-[40vh] w-full flex-col items-center justify-center gap-4 text-center">
        <p className="text-muted-foreground text-lg">This catch seems to have slipped away.</p>
        <Button asChild variant="default" className="bg-primary text-primary-foreground">
          <Link to="/HomePage">Back to the Docks</Link>
        </Button>
      </div>
    );
  }

  const product = products[0];
  const hasDiscount =
    product.discounted_price !== undefined &&
    product.discounted_price < product.price;

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
        {}
        <div className="space-y-4">
          <div className="bg-card border-border overflow-hidden rounded-2xl border shadow-lg">
            <AspectRatio ratio={1 / 1} className="bg-muted">
              {product.featured_image_url ? (
                <img
                  src={product.featured_image_url}
                  alt={product.title}
                  className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                  loading="eager"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
                  Seafood Image Coming Soon
                </div>
              )}
            </AspectRatio>
          </div>
        </div>

        {}
        <div className="flex flex-col gap-6">
          <div>
            <h1 className="text-foreground font-serif text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl">
              {product.title}
            </h1>
            
            <div className="mt-4 flex items-baseline gap-3">
              {hasDiscount ? (
                <>
                  <span className="text-primary text-3xl font-bold sm:text-4xl">
                    ${product.discounted_price?.toFixed(2)}
                  </span>
                  <span className="text-muted-foreground text-xl line-through decoration-2">
                    ${product.price.toFixed(2)}
                  </span>
                  <Badge variant="destructive" className="ml-2 text-sm bg-destructive text-destructive-foreground">
                    Fresh Catch Deal
                  </Badge>
                </>
              ) : (
                <span className="text-primary text-3xl font-bold sm:text-4xl">
                  ${product.price.toFixed(2)}
                </span>
              )}
            </div>
          </div>

          <Separator className="bg-border/60" />

          {product.short_description && (
            <div
              className="text-muted-foreground prose prose-neutral dark:prose-invert max-w-none text-base leading-relaxed"
              dangerouslySetInnerHTML={{ __html: product.short_description }}
            />
          )}

          <div className="py-4">
            <AddToCartButton
              productId={product.id}
              variant="default"
              size="lg"
              text="Add to Cooler"
              showQuantity={true}
              className="w-full sm:w-auto"
              buttonClassName="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md w-full sm:w-auto min-w-[200px] text-lg h-12 rounded-lg transition-all"
            />
          </div>

          <Separator className="bg-border/60" />

          <div className="space-y-4 text-sm">
            {product.categories && product.categories.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <Folder className="text-secondary h-4 w-4" />
                <span className="text-muted-foreground font-medium">Ocean Categories:</span>
                <div className="flex flex-wrap gap-2">
                  {product.categories.map((cat, i) => (
                    <Link key={cat.id} to={cat.link}>
                      <Badge variant="secondary" className="bg-secondary text-secondary-foreground hover:bg-secondary/80 cursor-pointer transition-colors px-3 py-1">
                        {cat.name}
                      </Badge>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {product.tags && product.tags.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <Tag className="text-secondary h-4 w-4" />
                <span className="text-muted-foreground font-medium">Seafood Tags:</span>
                <div className="flex flex-wrap gap-2">
                  {product.tags.map((tag, i) => (
                    <Link key={tag.id} to={tag.link}>
                      <span className="text-muted-foreground hover:text-primary transition-colors underline-offset-4 hover:underline">
                        #{tag.name}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {}
      {product.long_description && (
        <div className="mt-16 lg:mt-24">
          <div className="mb-8 flex items-center gap-4">
            <h2 className="text-foreground font-serif text-2xl font-bold sm:text-3xl">
              Catch Details & Origin
            </h2>
            <Separator className="flex-1 bg-border/60" />
          </div>
          <div
            className="prose prose-neutral dark:prose-invert prose-lg max-w-none text-muted-foreground leading-relaxed"
            dangerouslySetInnerHTML={{ __html: product.long_description }}
          />
        </div>
      )}
    </div>
  );
}

export default function SingleProductTemplateSection() {
  const [wpQuery, setWpQuery] = useState<any>(null);

  useEffect(() => {
    if (typeof window !== "undefined" && window.wvcClient) {
      const currentQuery = window.wvcClient.getCurrentWPQuery?.();
      setWpQuery(currentQuery);
    }
  }, []);

  
  
  if (!wpQuery) {
    return (
      <section id="single-product-template-section" className="bg-background py-12 md:py-20">
        <div className="container mx-auto px-4">
           <div className="flex min-h-[60vh] w-full items-center justify-center">
            <Loader className="text-primary h-10 w-10 animate-spin" />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="single-product-template-section" className="bg-background text-foreground py-12 md:py-20">
      <div className="container mx-auto px-4">
        <EcommerceProductsProvider postType="product" wp_query={wpQuery}>
          <SingleProductContent />
        </EcommerceProductsProvider>
      </div>
    </section>
  );
}